/*
  HUSTLESTREET — Product page (PHASE 11)
  File: assets/hs-product.js
  Loaded by: sections/hs-main-product.liquid, sections/hs-related-products.liquid

  <hs-product>
    - Variant selection from radio inputs [data-hs-option-input] inside fieldsets [data-hs-option]
    - Updates hidden variant id, URL, button states, option availability
    - Re-renders price / stock / sticky price through the Section Rendering API (hs-price stays the single source)
    - Moves the variant image to the first gallery position
    - Mobile gallery counter, quantity stepper, mobile sticky add-to-cart bar
    - Add to cart itself is handled by assets/hs-cart.js (form[data-hs-product-form])

  <hs-recommendations>
    - Lazy-loads Shopify product recommendations into sections/hs-related-products.liquid
*/
(function () {
  if (!('customElements' in window)) return;

  /* =========================================================
     <hs-product>
     ========================================================= */
  if (!customElements.get('hs-product')) {
    class HsProduct extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.url;
        this.updateUrl = this.dataset.updateUrl === 'true';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const json = this.querySelector('[data-hs-variants]');
        try {
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (error) {
          this.variants = [];
        }

        this.optionSets = Array.from(this.querySelectorAll('[data-hs-option]'));
        this.idInput = this.querySelector('[data-hs-variant-id]');
        this.atcButtons = Array.from(this.querySelectorAll('[data-hs-atc]'));
        this.track = this.querySelector('[data-hs-gallery-track]');
        this.counter = this.querySelector('[data-hs-gallery-current]');
        this.sticky = this.querySelector('[data-hs-sticky]');
        this.mainAtc = this.querySelector('form[data-hs-product-form] [data-hs-atc]');

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onTrackScroll = this.onTrackScroll.bind(this);

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        if (this.track) this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });

        this.setupSticky();
        this.updateAvailability(this.getSelectedOptions());
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        if (this.track) this.track.removeEventListener('scroll', this.onTrackScroll);
        if (this.stickyObserver) this.stickyObserver.disconnect();
        if (this.controller) this.controller.abort();
      }

      /* ---------- Events ---------- */

      onChange(event) {
        const input = event.target;

        if (input.matches('[data-hs-option-input]')) {
          this.onOptionChange();
          return;
        }

        if (input.matches('[data-hs-qty-input]')) {
          const value = parseInt(input.value, 10);
          input.value = isNaN(value) || value < 1 ? 1 : value;
        }
      }

      onClick(event) {
        const step = event.target.closest('[data-hs-qty-step]');
        if (!step) return;

        event.preventDefault();
        const input = this.querySelector('[data-hs-qty-input]');
        if (!input) return;

        const current = parseInt(input.value, 10) || 1;
        const delta = parseInt(step.dataset.hsQtyStep, 10) || 0;
        input.value = Math.max(1, current + delta);
      }

      onTrackScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateCounter();
        });
      }

      /* ---------- Variants ---------- */

      getSelectedOptions() {
        return this.optionSets.map((fieldset) => {
          const checked = fieldset.querySelector('[data-hs-option-input]:checked');
          return checked ? checked.value : null;
        });
      }

      onOptionChange() {
        const selected = this.getSelectedOptions();

        this.optionSets.forEach((fieldset, index) => {
          const label = fieldset.querySelector('[data-hs-option-selected]');
          if (label) label.textContent = selected[index] || '';
        });

        this.updateAvailability(selected);

        const variant = this.variants.find((v) =>
          v.options.every((value, index) => value === selected[index])
        );

        if (!variant) {
          if (this.idInput) this.idInput.value = '';
          this.setButtons('unavailable');
          return;
        }

        if (this.idInput) this.idInput.value = variant.id;
        this.setButtons(variant.available ? 'add' : 'soldout');

        if (this.updateUrl) {
          window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
        }

        if (variant.featured_media && variant.featured_media.id) {
          this.showMedia(variant.featured_media.id);
        }

        this.renderVariant(variant.id);
      }

      updateAvailability(selected) {
        this.optionSets.forEach((fieldset, optionIndex) => {
          fieldset.querySelectorAll('[data-hs-option-input]').forEach((input) => {
            const available = this.variants.some(
              (v) =>
                v.available &&
                v.options[optionIndex] === input.value &&
                v.options.every(
                  (value, j) => j === optionIndex || selected[j] == null || value === selected[j]
                )
            );

            const label = input.nextElementSibling;
            if (label) {
              label.classList.toggle('is-unavailable', !available);
              const srText = label.querySelector('[data-hs-unavailable-text]');
              if (srText) srText.hidden = available;
            }
          });
        });
      }

      setButtons(state) {
        this.atcButtons.forEach((button) => {
          const textEl = button.querySelector('[data-hs-atc-text]');
          const texts = {
            add: button.dataset.textAdd,
            soldout: button.dataset.textSoldout,
            unavailable: button.dataset.textUnavailable
          };
          if (textEl && texts[state]) textEl.textContent = texts[state];
          button.disabled = state !== 'add';
        });
      }

      async renderVariant(variantId) {
        if (!this.sectionId || !this.productUrl) return;

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        const url =
          this.productUrl +
          '?variant=' + encodeURIComponent(variantId) +
          '&section_id=' + encodeURIComponent(this.sectionId);

        try {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) return;

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          ['[data-hs-product-price]', '[data-hs-product-stock]', '[data-hs-sticky-price]'].forEach((selector) => {
            const fresh = doc.querySelector(selector);
            const current = this.querySelector(selector);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          });
        } catch (error) {
          /* Aborted or network error: buttons and variant id are already correct. */
        }
      }

      /* ---------- Gallery ---------- */

      showMedia(mediaId) {
        if (!this.track) return;

        const item = this.track.querySelector('[data-media-id="' + mediaId + '"]');
        if (!item) return;

        if (item !== this.track.firstElementChild) {
          this.track.prepend(item);
        }

        if (this.track.scrollWidth > this.track.clientWidth + 2) {
          this.track.scrollTo({ left: 0, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        }

        this.updateCounter();
      }

      updateCounter() {
        if (!this.track || !this.counter) return;
        const width = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / width) + 1;
        const total = this.track.children.length;
        this.counter.textContent = Math.min(Math.max(index, 1), total);
      }

      /* ---------- Sticky bar (mobile) ---------- */

      setupSticky() {
        if (!this.sticky || !this.mainAtc || !('IntersectionObserver' in window)) return;

        this.stickyObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;

          this.sticky.classList.toggle('is-visible', show);
          if (show) {
            this.sticky.removeAttribute('inert');
          } else {
            this.sticky.setAttribute('inert', '');
          }
        });

        this.stickyObserver.observe(this.mainAtc);
      }
    }

    customElements.define('hs-product', HsProduct);
  }

  /* =========================================================
     <hs-recommendations>
     ========================================================= */
  if (!customElements.get('hs-recommendations')) {
    class HsRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.loaded || !this.dataset.url) return;

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) {
                this.observer.disconnect();
                this.load();
              }
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this.observer.observe(this);
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        this.loaded = true;

        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error('Recommendations failed');

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('hs-recommendations');
          const inner = fresh ? fresh.querySelector('[data-hs-recs-inner]') : null;

          if (inner) {
            this.innerHTML = fresh.innerHTML;
            this.classList.add('is-loaded');
          } else if (!(window.Shopify && window.Shopify.designMode)) {
            this.hidden = true;
          }
        } catch (error) {
          if (!(window.Shopify && window.Shopify.designMode)) this.hidden = true;
        }
      }
    }

    customElements.define('hs-recommendations', HsRecommendations);
  }
})();